import {
  Situation,
  UserPreApprovedResponse,
  CreditLimitResponse,
  RequiredDocumentsResponse,
  MeResponse,
  CheckUnicoPayload,
  CheckUnicoResponse,
  CheckUnicoResultResponse,
  CheckUnicoReason,
} from '@types'
import { Service } from '@tino-frontend/adapters'
import { imageResize, requiredDocumentsTransform } from 'services/transformers'

export class UserService {
  private path = '/me'

  constructor(
    private readonly http: Service,
    private readonly httpMerchant: Service,
    private readonly storage: Service
  ) {}

  info(): Promise<MeResponse> {
    return this.http.get<unknown, MeResponse>(this.path)
  }

  situation(): Promise<Situation> {
    return this.httpMerchant.get(`/v1${this.path}/situation`)
  }

  async uploadSelf(payload: File) {
    try {
      const compressedFile = await imageResize(payload, 3000, 3000, 'jpeg', 100)
      const { url } = await this.http.get<unknown, { url: string }>(
        '/onboarding/self-photo'
      )
      await this.http.put(url, compressedFile)
      return this.http.post('/onboarding/self-photo')
    } catch (e: unknown) {
      return Promise.reject(e)
    }
  }

  async uploadBackDocument(payload: File) {
    try {
      const compressedFile = await imageResize(payload, 3000, 3000, 'jpeg', 100)
      const { url } = await this.http.get<unknown, { url: string }>(
        '/onboarding/back-document-photo'
      )
      await this.http.put(url, compressedFile)
      return this.http.post('/onboarding/back-document-photo')
    } catch (e: unknown) {
      return Promise.reject(e)
    }
  }

  async uploadFrontDocument(payload: File) {
    try {
      const compressedFile = await imageResize(payload, 3000, 3000, 'jpeg', 100)
      const { url } = await this.http.get<unknown, { url: string }>(
        '/onboarding/front-document-photo'
      )
      await this.http.put(url, compressedFile)
      return this.http.post('/onboarding/front-document-photo')
    } catch (e: unknown) {
      return Promise.reject(e)
    }
  }

  async checkUnico(
    payload: CheckUnicoPayload,
    type: CheckUnicoReason
  ): Promise<CheckUnicoResponse> {
    let url = ''
    switch (type) {
      case CheckUnicoReason.OWNER_BIOMETRY:
        url = '/v1/poa/checks'
        break
      case CheckUnicoReason.USER_VALIDATION:
      default:
        url = '/v1/checks'
        break
    }
    try {
      return this.httpMerchant.post(url, payload)
    } catch (e: unknown) {
      return Promise.reject(e)
    }
  }

  async checkUnicoResult(
    workflowId: string
  ): Promise<CheckUnicoResultResponse> {
    return this.httpMerchant.get(`/v1/checks/${workflowId}`)
  }

  preApproved(email: string): Promise<UserPreApprovedResponse> {
    return this.http.get(`/users/${email}`)
  }

  isPreUploadFeedbackFinished(): Promise<boolean> {
    return this.storage.get('user/isPreUploadFeedbackFinished')
  }

  getCreditLimit(
    documentType: string,
    documentNumber: string
  ): Promise<CreditLimitResponse> {
    return this.http.get(
      `${this.path}/companies/${documentType}/${documentNumber}/credit-limit`
    )
  }

  getRequiredDocuments(): Promise<RequiredDocumentsResponse> {
    return this.http
      .get<
        unknown,
        RequiredDocumentsResponse
      >(`${this.path}/required-documents`)
      .then(requiredDocumentsTransform)
  }

  byPassWhatsappOptIn(): Promise<void> {
    return this.http.post('/onboarding/bypass-whatsapp')
  }

  validatePrimaryPhone(source: string): Promise<void> {
    return this.httpMerchant.post('/v1/user/validate-primary-phone', {
      source,
    })
  }
}
